import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import { supplierIdRegex } from '../../../../utils/regex'
import { useConfigContext } from '../../../ui-config'
import { SupplierList } from '../../suppliers/data'
import { AddSupplierFormDto } from '../data/model'
import { AddSupplierView } from './AddSupplier.view'

export type FormFieldName = keyof AddSupplierFormDto

type Props = {
  initialData: AddSupplierFormDto
  suppliers: SupplierList | undefined
  isRequestRunning: boolean
  handleSubmit: (formValues: AddSupplierFormDto) => Promise<any>
}
export const AddSupplierForm: React.FC<Props> = ({
  initialData,
  suppliers,
  isRequestRunning,
  handleSubmit,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const [wrongSupportEmails, setWrongSupportEmails] = useState<string[] | null>(
    null,
  )
  const [wrongInboundEmails, setWrongInboundEmails] = useState<string[] | null>(
    null,
  )
  const [wrongOutboundEmails, setWrongOutboundEmails] = useState<
    string[] | null
  >(null)

  const separator = ';'

  const validateEmailField = (
    data: string,
    fieldErrors: FormikErrors<AddSupplierFormDto>,
    fieldName: FormFieldName,
    setWrongEmails: Function,
  ) => {
    const emailValidationRegex =
      /^[a-zA-Z0-9._%+-]+[a-zA-Z0-9]+@[a-zA-Z0-9]+[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const emails = data?.split(separator)
    const invalidEmails = emails?.filter(
      email => !emailValidationRegex.test(email),
    )
    if (invalidEmails?.length) {
      setWrongEmails(invalidEmails)
      fieldErrors[fieldName] = t(
        'settings.email.common.errorMessage.wrongEmail',
        { count: invalidEmails?.length },
      )
    } else {
      setWrongEmails(null)
    }
  }

  function isIdTaken(id: string, searchItems: SupplierList) {
    return searchItems.some(item => item.Id === id)
  }

  // formik
  const formik = useFormik<AddSupplierFormDto>({
    enableReinitialize: true,
    initialValues: initialData,
    validate: data => {
      let errors: FormikErrors<AddSupplierFormDto> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'id',
          config: config.selfservices.supplier.supplierInformation.id,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      if (!data.id) {
        errors.id = t('common.errors.required')
      } else if (!supplierIdRegex.test(data.id)) {
        errors.id = t('supplier.supplierInformation.form.id.errors.tooLong')
      } else if (suppliers && isIdTaken(data.id, suppliers)) {
        errors.id = t('supplier.supplierInformation.form.id.errors.idIsTaken')
      }

      if (!data.template) {
        errors.template = t('common.errors.required')
      }

      if (!data.name) {
        errors.name = t('common.errors.required')
      }

      if (data.supportValue)
        validateEmailField(
          data.supportValue,
          errors,
          'supportValue',
          setWrongSupportEmails,
        )

      if (data.inboundValue)
        validateEmailField(
          data.inboundValue,
          errors,
          'inboundValue',
          setWrongInboundEmails,
        )

      if (data.outboundValue)
        validateEmailField(
          data.outboundValue,
          errors,
          'outboundValue',
          setWrongOutboundEmails,
        )

      return errors
    },
    onSubmit: async (data: AddSupplierFormDto) => {
      await handleSubmit(data)
    },
  })

  const isFormFieldInvalid = (name: FormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <AddSupplierView
      formik={formik}
      isFormFieldInvalid={isFormFieldInvalid}
      getFormErrorMessage={getFormErrorMessage}
      wrongSupportEmails={wrongSupportEmails}
      wrongInboundEmails={wrongInboundEmails}
      wrongOutboundEmails={wrongOutboundEmails}
      separator={separator}
      isRequestRunning={isRequestRunning}
    />
  )
}
